import classNames from 'classnames';
import { Radio } from 'antd';

import { getChecklistsFor } from '../device-checklists';
import s from './symptoms-step.module.scss';

import type { FormikProps } from 'formik';
import type { FC, ReactNode } from 'react';
import type { ICriticalReadingData } from '../types';
import type { OutOfRangeParam } from '../engine/types';

interface Props {
  formik: FormikProps<ICriticalReadingData>;
  outOfRangeParams: OutOfRangeParam[];
  /** field `name`s that are required-but-empty — the row gets a red border. */
  errors?: string[];
}

/** One symptom row: [ Yes ] [ No ]  <question> on a single line. */
const SymptomRow: FC<{
  name: string;
  question: ReactNode;
  value: string | undefined;
  onChange: (val: string) => void;
}> = ({ name, question, value, onChange }) => (
  <div className={s.row}>
    <Radio.Group
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-cy={`symptom-${name}`}
      className={s['row-group']}
    >
      <Radio value="yes">Yes</Radio>
      <Radio value="no">No</Radio>
    </Radio.Group>
    <p className={s.question}>{question}</p>
  </div>
);

/**
 * #5 Signs & Symptoms — custom layout (radios first, question inline). Writes the
 * same Formik keys the engine/blurb read: each checklist question -> sym_* key,
 * plus symptomsOther / symptomsOtherText for the Other row.
 */
const SymptomsStep: FC<Props> = ({ formik, outOfRangeParams, errors = [] }) => {
  const checklists = getChecklistsFor(outOfRangeParams);
  const { values, setFieldValue } = formik;
  const errorSet = new Set(errors);

  return (
    <div className={s.wrapper}>
      <p className={s.heading}>#5 Signs and Symptoms</p>

      {checklists.map((cl) => {
        const cardError = cl.items.some((item) => errorSet.has(item.name));
        return (
          <div
            key={cl.key}
            className={classNames(s.checklist, { [s.error]: cardError })}
          >
            <p className={s.title}>
              {cl.title} <span className={s.required}>*</span>
            </p>
            {cl.items.map((item) => (
              <SymptomRow
                key={item.key}
                name={item.name}
                question={'title' in item ? item.title : item.name}
                value={values[item.name] as string | undefined}
                onChange={(val) => setFieldValue(item.name, val)}
              />
            ))}
          </div>
        );
      })}

      <div
        className={classNames(s.checklist, {
          [s.error]:
            errorSet.has('symptomsOther') || errorSet.has('symptomsOtherText'),
        })}
      >
        <p className={s.title}>
          Other <span className={s.required}>*</span>
        </p>
        <SymptomRow
          name="symptomsOther"
          question="Other symptoms not listed above?"
          value={values.symptomsOther}
          onChange={(val) => setFieldValue('symptomsOther', val)}
        />
        {values.symptomsOther === 'yes' && (
          <input
            className={s.specify}
            placeholder="Please specify"
            value={values.symptomsOtherText ?? ''}
            onChange={(e) => setFieldValue('symptomsOtherText', e.target.value)}
          />
        )}
      </div>
    </div>
  );
};

export default SymptomsStep;
